import { forwardRef } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  id?: string;
  name: string;
  value: readonly string[] | undefined;
  onChange: (next: string[]) => void;
  onBlur?: () => void;
  options: MultiSelectOption[];
  disabled?: boolean;
  invalid?: boolean;
  /** Inline error text, id-linked via aria-describedby to every option. */
  errorMessage?: string;
  /** Renders a "Loading options…" row in place of the checkbox list. */
  loading?: boolean;
  /** Text shown when `options` is empty and not loading. */
  emptyMessage?: string;
  /** Renders a "Clear all" button when at least one option is selected. */
  onClearAll?: () => void;
}

// A checkbox LIST over string[] — deliberately NOT a combobox and NOT
// `<select multiple>`. `<select multiple>` needs a non-obvious ctrl/cmd-click
// gesture to select more than one option (poor discoverability) and has
// weak mobile/a11y affordances; a searchable combobox (type-to-filter,
// chip-style selected list) would be the ideal UX once option counts grow,
// but that needs a dependency (react-select/downshift/similar) this story
// isn't taking — flagged as a later story if/when option counts demand it
// (see the /app/design "Promotion candidates" table). A checkbox list is
// fully native: Tab moves between options, Space toggles, and each option's
// checked state is announced individually by a screen reader — zero new
// dependencies for the common (short list) case.
//
// `ref` attaches to the FIRST checkbox (there is no single DOM node
// representing "the list") so RHF's `shouldFocusError` can still move focus
// here on a validation error.
export const MultiSelect = forwardRef<HTMLInputElement, MultiSelectProps>(function MultiSelect(
  { id, name, value, onChange, onBlur, options, disabled, invalid, errorMessage, loading = false, emptyMessage = 'No options available.', onClearAll },
  ref,
) {
  const selected = value ?? [];
  const toggle = (optionValue: string) => {
    if (selected.includes(optionValue)) onChange(selected.filter((v) => v !== optionValue));
    else onChange([...selected, optionValue]);
  };
  const errorId = errorMessage ? `${id ?? name}-error` : undefined;

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading options…</p>;
  }

  if (options.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div>
      {onClearAll && selected.length > 0 && (
        <button
          type="button"
          onClick={onClearAll}
          className="focus-ring mb-2 text-xs font-semibold text-primary hover:underline"
        >
          Clear all
        </button>
      )}
      <div className="space-y-2">
        {options.map((option, index) => {
          const optionId = `${id ?? name}-${index}`;
          return (
            <div key={option.value} className="flex items-center gap-2">
              <input
                ref={index === 0 ? ref : undefined}
                type="checkbox"
                id={optionId}
                name={`${name}[]`}
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
                onBlur={onBlur}
                disabled={disabled || option.disabled}
                aria-invalid={invalid || undefined}
                aria-describedby={errorId}
                className={`focus-ring h-4 w-4 shrink-0 rounded border-border accent-primary ${
                  invalid ? 'ring-2 ring-destructive' : ''
                }`}
              />
              <label htmlFor={optionId} className="min-w-0 text-sm text-foreground">
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      {invalid && errorMessage && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-destructive">
          {errorMessage}
        </p>
      )}
    </div>
  );
});
